import { useMemo } from 'react';
import { createAPIHooks, APIClient } from '@lifeomic/one-query';
import {
  FhirResourcesByName,
  PlatformDefaultBundle,
  SearchParamsForResourceType,
  WithPlatformDefaults,
} from '@lifeomic/service-clients/fhir-client';
import { Endpoints as PatientServiceEndpoints } from '@lifeomic/service-clients/patient-service';
import {
  FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
  formatCodingToken,
} from '@lifeomic/fhir-constants';
import { useActiveProjectId } from '../hooks';
import { usePhcRestAxios } from '../hooks/useRestAPIHooks';

export type FhirAPIEndpoints = {
  // POST /<resource>
  [Name in keyof FhirResourcesByName as `POST /v1/fhir/dstu3/${Name}`]: {
    Request: FhirResourcesByName[Name];
    Response: WithPlatformDefaults<FhirResourcesByName[Name]>;
  };
} & {
  // We specifically use :resourceId instead of :id here because the FHIR API
  // does a check between the path param property value and the data body
  // property value, and throws if they do not match. Since 'one-query' removes
  // any path params properties from the data body, having :id causes a failure.
  // PUT /<resource>/:resourceId
  [Name in keyof FhirResourcesByName as `PUT /v1/fhir/dstu3/${Name}/:resourceId`]: {
    Request: FhirResourcesByName[Name] & { id: string };
    Response: WithPlatformDefaults<FhirResourcesByName[Name]>;
  };
} & {
  // GET /<resource>/:id
  [Name in keyof FhirResourcesByName as `GET /v1/fhir/dstu3/${Name}/:id`]: {
    // eslint-disable-next-line @typescript-eslint/ban-types
    Request: {};
    Response: WithPlatformDefaults<FhirResourcesByName[Name]>;
  };
} & {
  // GET /<resource>
  [Name in keyof FhirResourcesByName as `GET /v1/fhir/dstu3/${Name}`]: {
    Request: SearchParamsForResourceType<Name>;
    Response: PlatformDefaultBundle<
      WithPlatformDefaults<FhirResourcesByName[Name]>
    >;
  };
} & {
  // POST /<resource>/_search
  [Name in keyof FhirResourcesByName as `POST /v1/fhir/dstu3/${Name}/_search`]: {
    Request: {
      [key in keyof SearchParamsForResourceType<Name>]: key extends 'pageSize'
        ? string
        : SearchParamsForResourceType<Name>[key];
    };
    Response: PlatformDefaultBundle<
      WithPlatformDefaults<FhirResourcesByName[Name]>
    >;
  };
};

type Endpoints = FhirAPIEndpoints & PatientServiceEndpoints;

export const FHIR_API_HOOKS_NAME = 'fhir';

const hooks = createAPIHooks<Endpoints>({
  name: FHIR_API_HOOKS_NAME,
  client: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return usePhcRestAxios();
  },
});

export const useFHIRQuery = hooks.useAPIQuery;

export const useInfiniteFHIRQuery = hooks.useInfiniteAPIQuery;

export const useFHIRMutation = hooks.useAPIMutation;

export const useFHIRCombinedQueries = hooks.useCombinedAPIQueries;

export const useFHIRCache = hooks.useAPICache;

/**
 * Typed FHIR + patient-service client that tracks the active account/auth.
 * Prefer this over module-level singletons in React code.
 */
export const useFhirClient = (): APIClient<Endpoints> => {
  const client = usePhcRestAxios();
  return useMemo(() => new APIClient<Endpoints>(client), [client]);
};

export const useDataset = () => {
  const projectId = useActiveProjectId();

  const codingToken = formatCodingToken({
    system: FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
    code: projectId,
  }) as `${typeof FHIR_DATASET_TAG_SYSTEM_LIFEOMIC}|${string}`;

  return {
    id: projectId,
    tag: {
      system: FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
      code: projectId,
    },
    codingToken,
  } as const;
};
